/**
 * ShipmentDocumentsTab — Warehouse Document Center.
 *
 * Root cause fix (Jun 2026):
 *   1. De documentselector zocht via `entity_search` op entity-types
 *      `packing_list` / `sales_invoice` / `booking_form`. Die adapters
 *      bestonden niet → altijd 0 resultaten. Nu loopt de zoekactie via het
 *      canonical endpoint `GET /admin/warehouse-shipments/{id}/documents/search`
 *      dat hard op deze zending is gescopeerd.
 *   2. Handmatige invoer vroeg om een interne UUID. Nu accepteert hetzelfde
 *      zoekveld een paklijstnummer, factuurnummer, CR-nummer, boekings- of
 *      offertenummer, W-nummer én UUID — allemaal via DEZELFDE resolver.
 *   3. Bekijken gebruikte `window.open(blobUrl)` → leeg tabblad in Chrome.
 *      Nu inline via `DocumentPreviewDialog`.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, Download, Eye, FileText, Link as LinkIcon,
  Loader2, Printer, ScanBarcode, Search, Send, Trash2, Upload, Zap, Unlink, Pencil, RotateCcw,
} from "lucide-react";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";
import DocumentPreviewDialog from "../shared/DocumentPreviewDialog";
import SendDocumentDialog from "./SendDocumentDialog";
import SystemDocumentsSection from "./SystemDocumentsSection";
import DocumentEditDialog from "./DocumentEditDialog";
import { CustomsInvoiceDialog } from "./CustomsInvoiceDialog";

const SOURCE_LABELS = {
  packing_list: "Paklijst",
  booking_form: "Boekingsformulier",
  warehouse_receipt: "Warehouse Receipt",
  sales_invoice: "Factuur / Creditnota",
  value_invoice: "Waardefactuur",
  customs_invoice: "Douanefactuur",
  quote: "Offerte",
  document_upload: "CMS-document",
  gridfs: "Directe upload",
};

const KIND_OPTIONS = [
  { key: "", label: "Alle documentsoorten" },
  { key: "packing_list", label: "Paklijst" },
  { key: "sales_invoice", label: "Factuur" },
  { key: "credit_note", label: "Creditnota" },
  { key: "booking_form", label: "Boekingsformulier" },
  { key: "quote", label: "Offerte" },
];

const QUICK_SEND_ITEMS = [
  { doc_type: "warehouse_receipt", label: "Warehouse Receipt", icon: ScanBarcode },
  { doc_type: "packing_list", label: "Paklijst", icon: FileText },
  { doc_type: "value_invoice", label: "Waardefactuur", icon: FileText },
  { doc_type: "manifest", label: "Manifest", icon: FileText },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
}

function apiPath(url) {
  return (url || "").replace(/^\/api/, "");
}

/* Canonical documenten hebben geen link-record; ze zijn beschikbaar omdat de
   canonical renderer ze kan produceren. Dat maken we in de UI expliciet. */
const KIND_BADGES = {
  system: { label: "Systeem", cls: "bg-blue-100 text-blue-800" },
  canonical: { label: "Automatisch", cls: "bg-emerald-100 text-emerald-800" },
};

export default function ShipmentDocumentsTab({ shipment }) {
  const { hasPermission } = useAuth();
  const canLink = hasPermission("shipments:edit") || hasPermission("quotes:edit");
  const canUnlink = hasPermission("shipments:manage") || hasPermission("quotes:edit");
  const canSend = hasPermission("shipments:edit") || hasPermission("quotes:edit");

  const [docs, setDocs] = useState([]);
  const [canonicalDocs, setCanonicalDocs] = useState([]);
  const [availableDocTypes, setAvailableDocTypes] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [missing, setMissing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [preselectedKeys, setPreselectedKeys] = useState([]);
  const [autoLinking, setAutoLinking] = useState(false);
  const [preview, setPreview] = useState(null);
  const [excludedDocs, setExcludedDocs] = useState([]);
  const [unlinkTarget, setUnlinkTarget] = useState(null);
  const [unlinkBusy, setUnlinkBusy] = useState(false);
  const [editValue, setEditValue] = useState(null);
  const [customsDoc, setCustomsDoc] = useState(null);

  const shipmentId = shipment?.id;

  const load = useCallback(async () => {
    if (!shipmentId) return;
    setLoading(true);
    try {
      const [rDocs, rMiss] = await Promise.all([
        adminApi.get(`/admin/warehouse-shipments/${shipmentId}/documents`),
        adminApi.get(`/admin/warehouse-shipments/${shipmentId}/documents/missing`),
      ]);
      setDocs(rDocs.data.documents || []);
      setExcludedDocs(rDocs.data.excluded_documents || []);
      setCanonicalDocs(rDocs.data.canonical_documents || []);
      setAvailableDocTypes(rDocs.data.available_doc_types || []);
      setDocTypes(rDocs.data.doc_types || []);
      setMissing(rMiss.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [shipmentId]);

  useEffect(() => { load(); }, [load]);

  /* ÉÉN lijst uit drie bronnen:
       A. canonical systeemdocumenten (Warehouse Receipt, Shipping Label)
       B. canonical relatiedocumenten (paklijst, waardefactuur, creditnota…)
       C. expliciete `shipment_documents`-links en uploads
     De backend dedupliceert canonical documenten die óók expliciet gekoppeld
     zijn (dan blijft de link-entry over), dus hier ontstaan geen dubbele
     regels. Canonical IDs (`{type}:{source_id}`) zijn stabiel. */
  const items = useMemo(() => {
    const labelFor = (key, fallback) =>
      (docTypes.find((t) => t.key === key) || {}).label || fallback || key;
    const canonical = canonicalDocs.map((c) => ({
      key: c.document_id,
      documentId: c.document_id,
      source: c.source, sourceId: c.source_id,
      relationKind: c.relation_kind, canUnlink: c.can_unlink,
      version: c,
      linkId: null,
      kind: c.kind,
      docType: c.doc_type,
      docTypeLabel: labelFor(c.doc_type, c.doc_type_label),
      title: c.title || c.doc_type_label,
      number: c.number,
      description: c.description,
      renderable: c.renderable !== false,
      renderError: c.render_error,
      viewUrl: apiPath(c.view_url),
      downloadUrl: apiPath(c.download_url),
      filename: `${c.doc_type}-${c.number || "document"}.pdf`,
    }));
    const linked = docs.map((l) => {
      const dm = l.display_meta || {};
      return {
        key: `link:${l.id}`,
        documentId: null,
        source: l.source, sourceId: l.source_id,
        relationKind: l.relation_kind, canUnlink: l.can_unlink,
        version: l.display_meta,
        linkId: l.id,
        kind: "link",
        docType: l.doc_type || "other",
        docTypeLabel: labelFor(l.doc_type, l.doc_type_label),
        title: l.filename || dm.invoice_number || dm.quote_number
          || dm.receipt_number || SOURCE_LABELS[l.source] || "Document",
        number: dm.invoice_number || dm.quote_number || dm.receipt_number || null,
        description: l.description,
        renderable: true,
        renderError: null,
        viewUrl: `/admin/warehouse-shipments/${shipmentId}/documents/${l.id}/view`,
        downloadUrl: `/admin/warehouse-shipments/${shipmentId}/documents/${l.id}/download`,
        filename: l.filename || `document-${l.id}.pdf`,
        link: l,
      };
    });
    return [...canonical, ...linked];
  }, [canonicalDocs, docs, docTypes, shipmentId]);

  const handleAutoLink = async () => {
    setAutoLinking(true);
    try {
      const r = await adminApi.post(`/admin/warehouse-shipments/${shipmentId}/documents/auto-link`);
      if (r.data.linked_count > 0) {
        toast.success(`${r.data.linked_count} document${r.data.linked_count > 1 ? "en" : ""} automatisch gekoppeld`);
        await load();
      } else if (r.data.skipped_ambiguous?.length > 0) {
        toast(`Meerdere kandidaten gevonden: ${r.data.skipped_ambiguous.join(", ")} — koppel handmatig`);
      } else {
        toast("Geen bestaande documenten gevonden om te koppelen");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAutoLinking(false);
    }
  };

  const handleUnlink = async () => {
    if (!unlinkTarget) return;
    setUnlinkBusy(true);
    try {
      if (unlinkTarget.linkId) {
        await adminApi.delete(`/admin/warehouse-shipments/${shipmentId}/documents/${unlinkTarget.linkId}`);
      } else {
        await adminApi.post(`/admin/warehouse-shipments/${shipmentId}/documents/exclude`, null,
          { params: { document_id: unlinkTarget.documentId } });
      }
      setUnlinkTarget(null);
      toast.success("Koppeling verwijderd; bronbestand bewaard");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setUnlinkBusy(false); }
  };

  const relink = async (link) => {
    try { await adminApi.post(`/admin/warehouse-shipments/${shipmentId}/documents/${link.id}/relink`); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const editDocument = async (item) => {
    try {
      const { data } = await adminApi.get(`/admin/invoices/${item.sourceId}`);
      setEditValue(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const openCustoms = async (item) => {
    try { const { data } = await adminApi.get(`/admin/customs-invoices/${item.sourceId}`); setCustomsDoc(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openPreview = (item, { autoPrint = false } = {}) => {
    if (!item.renderable) {
      toast.error(item.renderError
        ? `Document kan niet worden gerenderd: ${item.renderError}`
        : "Document is niet beschikbaar");
      return;
    }
    setPreview({
      title: item.title,
      subtitle: [item.docTypeLabel, item.number, item.description]
        .filter(Boolean).join(" · "),
      url: item.viewUrl,
      downloadUrl: item.downloadUrl,
      filename: item.filename,
      autoPrint,
    });
  };

  const handleDownload = async (item) => {
    if (!item.renderable) {
      toast.error("Document is niet beschikbaar");
      return;
    }
    try {
      const resp = await adminApi.get(item.downloadUrl, { responseType: "blob" });
      if (!resp.data || resp.data.size === 0) {
        throw new Error("Document is leeg (0 bytes)");
      }
      const objUrl = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = objUrl;
      const cd = resp.headers?.["content-disposition"] || "";
      const match = cd.match(/filename="([^"]+)"/);
      a.download = match ? match[1] : item.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.setTimeout(() => window.URL.revokeObjectURL(objUrl), 60000);
      toast.success("Download gestart");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Download mislukt");
    }
  };

  /* Snel verzenden werkt op canonical BESCHIKBAARHEID — niet op de vraag of
     er een `shipment_documents`-record bestaat. */
  const handleQuickSend = (docType) => {
    const matches = items.filter((i) => i.docType === docType && i.renderable);
    if (matches.length === 0) {
      toast.error(`Geen ${docType.replace(/_/g, " ")} beschikbaar voor deze zending.`);
      return;
    }
    setPreselectedKeys(matches.length === 1 ? [matches[0].key] : []);
    if (matches.length > 1) toast.info("Meerdere documenten beschikbaar. Selecteer bewust het juiste document.");
    setSendDialogOpen(true);
  };

  const handleSendMulti = () => {
    setPreselectedKeys([]);
    setSendDialogOpen(true);
  };

  const groupedByType = useMemo(() => {
    const groups = {};
    items.forEach((d) => {
      const key = d.docType || "other";
      groups[key] = groups[key] || [];
      groups[key].push(d);
    });
    return groups;
  }, [items]);

  return (
    <div className="space-y-4" data-testid="shipment-documents-tab">
      <SystemDocumentsSection
        shipment={shipment}
        onOpenReceiptPreview={() => window.dispatchEvent(new CustomEvent("shipment:open-warehouse-receipt", { detail: { shipmentId: shipment?.id } }))}
      />

      {missing && !missing.is_complete && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-2"
             data-testid="shipment-docs-missing-banner">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-xs text-amber-900">
            <div className="font-bold">Verplichte documenten ontbreken ({missing.service_type})</div>
            <div className="mt-0.5">Nog toevoegen: {missing.missing_labels.join(", ")}</div>
          </div>
        </div>
      )}
      {missing && missing.is_complete && items.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 flex items-start gap-2"
             data-testid="shipment-docs-complete-banner">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-emerald-900 font-medium">
            Alle verplichte documenten voor {missing.service_type} zijn gekoppeld
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {canLink && (
          <>
            <Button size="sm" onClick={() => setLinkDialogOpen(true)} variant="outline"
                    className="rounded-none text-xs" data-testid="shipment-docs-link-btn">
              <LinkIcon className="w-3.5 h-3.5 mr-1" /> Bestaand koppelen
            </Button>
            <Button size="sm" onClick={() => setUploadDialogOpen(true)} variant="outline"
                    className="rounded-none text-xs" data-testid="shipment-docs-upload-btn">
              <Upload className="w-3.5 h-3.5 mr-1" /> Nieuw uploaden
            </Button>
            <Button size="sm" onClick={handleAutoLink} disabled={autoLinking} variant="outline"
                    className="rounded-none text-xs" data-testid="shipment-docs-auto-link-btn">
              {autoLinking ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1" />}
              Auto-koppel
            </Button>
          </>
        )}
        {canSend && items.length > 0 && (
          <Button size="sm" onClick={handleSendMulti}
                  className="rounded-none text-xs bg-[#0055FF] text-white hover:bg-[#0044CC] ml-auto"
                  data-testid="shipment-docs-send-btn">
            <Send className="w-3.5 h-3.5 mr-1" /> Verstuur document
          </Button>
        )}
      </div>

      {canSend && items.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center bg-slate-50 border border-slate-200 p-2 rounded">
          <span className="text-[10px] uppercase font-bold text-slate-600 tracking-wide">Snel verzenden:</span>
          {QUICK_SEND_ITEMS.map((qs) => {
            const has = availableDocTypes.includes(qs.doc_type);
            const Icon = qs.icon;
            return (
              <button
                key={qs.doc_type} type="button"
                onClick={() => handleQuickSend(qs.doc_type)} disabled={!has}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border rounded transition-colors ${
                  has
                    ? "bg-white border-slate-200 hover:border-[#0055FF] hover:bg-blue-50 text-[#082c50]"
                    : "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed"
                }`}
                data-testid={`quick-send-${qs.doc_type}`}
                title={has ? `Verzend ${qs.label}` : `${qs.label} niet beschikbaar voor deze zending`}
              >
                <Icon className="w-3.5 h-3.5" />
                {qs.label}
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-500 text-sm">
          <Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Documenten laden…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-gray-50 border border-dashed border-gray-300 p-8 rounded-md text-center"
             data-testid="shipment-docs-empty">
          <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <h3 className="font-chivo font-bold text-[#082c50]">Nog geen documenten</h3>
          <p className="text-sm text-gray-600 mt-1">
            Koppel bestaande documenten (paklijst, boekingsformulier, factuur, offerte) of
            upload een bestand rechtstreeks (Manifest, Bill of Lading, foto&apos;s).
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="shipment-docs-list">
          {Object.entries(groupedByType).map(([docType, group]) => (
            <div key={docType} className="border border-slate-200 rounded-md overflow-hidden">
              <div className="bg-slate-50 px-3 py-1.5 flex items-center justify-between border-b border-slate-200">
                <span className="text-[10px] uppercase font-bold text-slate-700 tracking-wide">
                  {(docTypes.find((t) => t.key === docType) || {}).label || docType}
                </span>
                <span className="text-[10px] text-slate-500 tabular-nums">{group.length}</span>
              </div>
              <ul>
                {group.map((item) => (
                  <DocumentRow
                    key={item.key} item={item} canUnlink={canUnlink}
                    onView={(i) => openPreview(i)}
                    onPrint={(i) => openPreview(i, { autoPrint: true })}
                    onDownload={handleDownload} onUnlink={setUnlinkTarget}
                    canEditValue={hasPermission("invoices:edit")} onEdit={editDocument} onCustoms={openCustoms}
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      <DocumentPreviewDialog
        open={!!preview}
        onClose={() => setPreview(null)}
        title={preview?.title}
        subtitle={preview?.subtitle}
        url={preview?.url}
        downloadUrl={preview?.downloadUrl}
        fallbackFilename={preview?.filename}
        autoPrint={!!preview?.autoPrint}
      />

      {linkDialogOpen && (
        <LinkExistingDocumentDialog
          shipmentId={shipmentId}
          shipment={shipment}
          docTypes={docTypes}
          onClose={() => setLinkDialogOpen(false)}
          onLinked={async () => { setLinkDialogOpen(false); await load(); }}
        />
      )}

      {uploadDialogOpen && (
        <UploadDocumentDialog
          shipmentId={shipmentId}
          docTypes={docTypes}
          onClose={() => setUploadDialogOpen(false)}
          onUploaded={async () => { setUploadDialogOpen(false); await load(); }}
        />
      )}

      {sendDialogOpen && (
        <SendDocumentDialog
          shipment={shipment} documents={items.filter((item) => item.docType !== "customs_invoice")}
          preselectedKeys={preselectedKeys}
          onClose={() => setSendDialogOpen(false)}
          onSent={async () => { setSendDialogOpen(false); await load(); }}
        />
      )}
      {excludedDocs.length > 0 && <section className="mt-4 border-t pt-3" data-testid="shipment-excluded-documents">
        <h3 className="text-sm font-semibold">Ontkoppeld</h3>
        {excludedDocs.map((link) => <div key={link.id} className="flex flex-wrap items-center justify-between gap-2 py-2" data-testid={`excluded-document-${link.id}`}>
          <span className="min-w-0 break-all text-xs">{link.filename || link.display_meta?.invoice_number || link.doc_type_label}</span>
          {canUnlink && <Button size="sm" variant="ghost" onClick={() => relink(link)} data-testid={`document-relink-${link.id}`}><RotateCcw className="mr-1 h-3 w-3" />Opnieuw koppelen</Button>}
        </div>)}
      </section>}
      <Dialog open={!!unlinkTarget} onOpenChange={(open) => { if (!open && !unlinkBusy) setUnlinkTarget(null); }}>
        <DialogContent data-testid="document-unlink-dialog">
          <DialogHeader><DialogTitle>Document ontkoppelen?</DialogTitle>
            <DialogDescription data-testid="document-unlink-source-preserved">Alleen de relatie met deze zending wordt verwijderd. Het bronbestand blijft bestaan en de uitsluiting blijft na refresh behouden.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" disabled={unlinkBusy} onClick={() => setUnlinkTarget(null)} data-testid="document-unlink-cancel">Annuleren</Button>
            <Button disabled={unlinkBusy} onClick={handleUnlink} data-testid="document-unlink-confirm">Ontkoppelen</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {editValue && <DocumentEditDialog open docType="generated_invoice" doc={editValue} onOpenChange={(open) => { if (!open) setEditValue(null); }} onSaved={async () => { setEditValue(null); await load(); }} />}
      {customsDoc && <CustomsInvoiceDialog document={customsDoc} onClose={() => { setCustomsDoc(null); load(); }} />}
    </div>
  );
}

/* ─── Document row ─────────────────────────────────────────── */
function DocumentRow({ item, canUnlink, onView, onPrint, onDownload, onUnlink, canEditValue, onEdit, onCustoms }) {
  const link = item.link;
  const badge = KIND_BADGES[item.kind];
  const srcLabel = link ? (SOURCE_LABELS[link.source] || link.source) : item.docTypeLabel;
  const dm = link?.display_meta || {};
  const disabled = !item.renderable;
  return (
    <li className="px-3 py-2 border-b border-slate-100 last:border-b-0 flex items-center gap-3 hover:bg-slate-50/60"
        data-testid={`document-row-${item.key}`}>
      <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-sm font-medium text-[#082c50] truncate">
            {item.title}
          </span>
          {badge && (
            <span className={`text-[9px] px-1 py-0.5 rounded uppercase font-bold tracking-wide ${badge.cls}`}
                  data-testid={`document-badge-${item.key}`}>
              {badge.label}
            </span>
          )}
          {link?.auto_linked && (
            <span className="text-[9px] px-1 py-0.5 bg-blue-100 text-blue-700 rounded uppercase font-bold tracking-wide">Auto</span>
          )}
        </div>
        <div className="text-[11px] text-slate-500 flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
          <span>{srcLabel}</span>
          {item.description && <span>· {item.description}</span>}
          {link?.size_bytes && <span>· {Math.round(link.size_bytes / 1024)} KB</span>}
          {dm.total && <span>· {dm.currency || "EUR"} {Number(dm.total).toFixed(2)}</span>}
          {dm.status && <span>· status: {dm.status}</span>}
          {link
            ? <span className="ml-auto">gekoppeld {fmtDate(link.linked_at)} door {link.linked_by || "—"}</span>
            : <span className="ml-auto">{item.relationKind === "inherent" ? "Eigen systeemdocument" : "Via aantoonbare bronrelatie"}</span>}
          <span data-testid={`document-relation-${item.key}`}>{({ inherent: "Systeemdocument", explicit: "Expliciet gekoppeld", suggested: "Automatisch voorgesteld", upload: "Upload" })[item.relationKind] || "Gekoppeld"}</span>
          {item.version?.version_number && <span data-testid={`document-version-${item.key}`}>v{item.version.version_number} · revisie {item.version.revision || 0} · {fmtDate(item.version.version_date || item.version.updated_at || item.version.created_at)}</span>}
        </div>
        {disabled && (
          <div className="text-[10px] text-red-700 mt-0.5" data-testid={`document-unrenderable-${item.key}`}>
            Kan niet worden gerenderd: {item.renderError || "onbekende fout"}
          </div>
        )}
        {link?.last_sent_at && (
          <div className="text-[10px] text-emerald-700 mt-0.5">
            Laatst verzonden: {fmtDate(link.last_sent_at)}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {item.source === "value_invoice" && canEditValue && <button type="button" onClick={() => onEdit(item)} className="rounded p-1.5 text-slate-500 hover:bg-blue-50" title="Waardefactuur bewerken" data-testid={`document-edit-value-${item.key}`}><Pencil className="h-4 w-4" /></button>}
        {item.source === "customs_invoice" && <button type="button" onClick={() => onCustoms(item)} className="rounded p-1.5 text-slate-500 hover:bg-blue-50" title="Douanefactuur openen" data-testid={`document-open-customs-${item.key}`}><FileText className="h-4 w-4" /></button>}
        <button type="button" onClick={() => onView(item)} disabled={disabled}
                className="p-1.5 text-slate-500 hover:text-[#0055FF] hover:bg-blue-50 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                title="Bekijken" data-testid={`document-view-${item.key}`}>
          <Eye className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => onPrint(item)} disabled={disabled}
                className="p-1.5 text-slate-500 hover:text-[#0055FF] hover:bg-blue-50 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                title="Printen" data-testid={`document-print-${item.key}`}>
          <Printer className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => onDownload(item)} disabled={disabled}
                className="p-1.5 text-slate-500 hover:text-[#0055FF] hover:bg-blue-50 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                title="Downloaden" data-testid={`document-download-${item.key}`}>
          <Download className="w-4 h-4" />
        </button>
        {canUnlink && item.canUnlink && (
          <button type="button" onClick={() => onUnlink(item)}
                  className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
                  title="Ontkoppelen — bronbestand behouden" data-testid={`document-unlink-${item.linkId || item.key}`}>
            <Unlink className="w-4 h-4" />
          </button>
        )}
      </div>
    </li>
  );
}

/* ─── Bestaand document koppelen (canonical resolver) ────────── */
function LinkExistingDocumentDialog({ shipmentId, shipment, docTypes, onClose, onLinked }) {
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState("");
  const [results, setResults] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selected, setSelected] = useState(null);
  const [docType, setDocType] = useState("packing_list");
  const [busy, setBusy] = useState(false);

  const search = useCallback(async (q, k) => {
    setSearching(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (k) params.set("kinds", k);
      params.set("limit", "25");
      const r = await adminApi.get(
        `/admin/warehouse-shipments/${shipmentId}/documents/search?${params.toString()}`,
      );
      setResults(r.data.results || []);
      setRejected(r.data.rejected || []);
      setSearched(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSearching(false);
    }
  }, [shipmentId]);

  // Bij openen: toon direct alle documenten die bij deze zending horen.
  useEffect(() => { search("", ""); }, [search]);

  const handleSelect = (item) => {
    setSelected(item);
    setDocType(item.default_doc_type || "other");
  };

  const handleLink = async () => {
    if (!selected) { toast.error("Selecteer een document"); return; }
    setBusy(true);
    try {
      await adminApi.post(`/admin/warehouse-shipments/${shipmentId}/documents/link`, {
        source: selected.link_source,
        source_id: selected.id,
        doc_type: docType,
      });
      toast.success(`${selected.kind_label} ${selected.number} gekoppeld`);
      onLinked();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  // Handmatige invoer: exact dezelfde backend resolver via document_ref.
  const handleLinkByRef = async () => {
    if (!query.trim()) { toast.error("Voer een documentnummer in"); return; }
    setBusy(true);
    try {
      await adminApi.post(`/admin/warehouse-shipments/${shipmentId}/documents/link`, {
        document_ref: query.trim(),
      });
      toast.success(`Document ${query.trim()} gekoppeld`);
      onLinked();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.candidates) {
        setResults(detail.candidates.map((c) => ({ ...c, already_linked: false })));
        setSearched(true);
        toast(detail.message || "Meerdere documenten gevonden — kies het juiste");
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl" data-testid="link-document-dialog">
        <DialogHeader>
          <DialogTitle>Bestaand document koppelen</DialogTitle>
          <DialogDescription>
            Zoek op documentnummer, W-nummer, klantnaam of e-mail. Alleen documenten
            die bij zending {shipment?.tracking_number || shipmentId} of bij dezelfde
            klant horen worden getoond. Documenten worden nooit gedupliceerd.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs">Zoeken / documentnummer</Label>
              <input
                type="text" value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(query, kind); } }}
                placeholder="Bijv. INV-2026-2199, W5999NL261784 of klantnaam"
                className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm"
                data-testid="link-doc-search-input"
              />
            </div>
            <div className="w-[190px]">
              <Label className="text-xs">Soort</Label>
              <Select value={kind || "__all__"}
                      onValueChange={(v) => { const nv = v === "__all__" ? "" : v; setKind(nv); search(query, nv); }}>
                <SelectTrigger data-testid="link-doc-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {KIND_OPTIONS.map((k) => (
                    <SelectItem key={k.key || "__all__"} value={k.key || "__all__"}>{k.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              size="sm" variant="outline" onClick={() => search(query, kind)}
              disabled={searching} className="rounded-none text-xs h-[34px]"
              data-testid="link-doc-search-btn"
            >
              {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
            </Button>
          </div>

          <div className="border border-slate-200 max-h-[280px] overflow-auto"
               data-testid="link-doc-results">
            {searching ? (
              <div className="p-6 text-center text-slate-500 text-xs">
                <Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Zoeken…
              </div>
            ) : results.length === 0 ? (
              <div className="p-6 text-center text-xs text-slate-500"
                   data-testid="link-doc-no-results">
                {searched && query
                  ? <>Geen document gevonden voor <span className="font-mono">{query}</span> bij deze zending.</>
                  : "Geen bestaande documenten gevonden voor deze zending."}
                {rejected.length > 0 && (
                  <div className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 p-2 text-left"
                       data-testid="link-doc-rejected">
                    {rejected.length} document(en) met dit nummer bestaan wél, maar horen bij een
                    andere klant/zending en kunnen daarom niet worden gekoppeld.
                  </div>
                )}
              </div>
            ) : (
              <ul>
                {results.map((item) => (
                  <li key={`${item.kind}:${item.id}`}>
                    <button
                      type="button" onClick={() => handleSelect(item)}
                      disabled={item.already_linked}
                      className={`w-full text-left px-3 py-2 border-b border-slate-100 last:border-b-0 flex items-center gap-3 transition-colors ${
                        selected?.id === item.id && selected?.kind === item.kind
                          ? "bg-blue-50"
                          : item.already_linked ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-50"
                      }`}
                      data-testid={`link-doc-result-${item.id}`}
                    >
                      <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 bg-slate-100 text-slate-700 flex-shrink-0">
                        {item.kind_label}
                      </span>
                      <span className="font-mono text-xs text-[#082c50] flex-shrink-0">{item.number}</span>
                      <span className="text-[11px] text-slate-500 truncate flex-1">
                        {[item.customer_name !== "-" ? item.customer_name : null,
                          item.w_number, item.email].filter(Boolean).join(" · ")}
                      </span>
                      {item.already_linked && (
                        <span className="text-[9px] uppercase font-bold text-emerald-700 flex-shrink-0">
                          Gekoppeld
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {selected && (
            <div className="text-xs bg-emerald-50 border border-emerald-200 px-2 py-1.5 text-emerald-800"
                 data-testid="link-doc-selected">
              ✓ Geselecteerd: <strong>{selected.kind_label} {selected.number}</strong>
            </div>
          )}

          <div>
            <Label className="text-xs">Documenttype (voor missing-check)</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="link-doctype-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {docTypes.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          {!selected && query.trim() && (
            <Button
              variant="outline" onClick={handleLinkByRef} disabled={busy}
              className="rounded-none text-xs" data-testid="link-doc-by-ref"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <LinkIcon className="w-4 h-4 mr-1" />}
              Koppel op nummer
            </Button>
          )}
          <Button
            onClick={handleLink} disabled={busy || !selected}
            className="bg-[#0055FF] text-white" data-testid="link-doc-confirm"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <LinkIcon className="w-4 h-4 mr-1" />}
            Koppelen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Upload dialog ─────────────────────────────────────── */
function UploadDocumentDialog({ shipmentId, docTypes, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState("other");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const handleUpload = async () => {
    if (!file) { toast.error("Selecteer een bestand"); return; }
    setBusy(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("doc_type", docType);
      if (description.trim()) formData.append("description", description.trim());
      await adminApi.post(
        `/admin/warehouse-shipments/${shipmentId}/documents/upload`,
        formData, { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success("Document geüpload");
      onUploaded();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="upload-document-dialog">
        <DialogHeader>
          <DialogTitle>Aanvullend document uploaden</DialogTitle>
          <DialogDescription>
            Bijv. aankoopfactuur, bon, extra paklijst, verklaring, douane- of
            transportdocument. Uploads &lt;= 50 MB. Extensies: PDF, Word, Excel,
            PNG, JPG, HEIC, TIFF, ZIP, XML, CSV, TXT.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Documenttype</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="upload-doctype-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {docTypes.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Omschrijving (optioneel)</Label>
            <input
              type="text" value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm"
              placeholder="Bijv. Aankoopfactuur leverancier"
              data-testid="upload-description-input"
            />
          </div>
          <div>
            <Label className="text-xs">Bestand</Label>
            <input
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full mt-1 text-xs"
              data-testid="upload-file-input"
            />
            {file && (
              <div className="mt-1 text-xs text-slate-600 font-mono">
                {file.name} ({Math.round(file.size / 1024)} KB)
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button onClick={handleUpload} disabled={busy || !file}
                  className="bg-[#0055FF] text-white" data-testid="upload-doc-confirm">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
            Uploaden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
